import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users,
  Calendar,
  FileText,
  Settings,
  LogOut,
  Menu,
  X,
  Shield,
  Bell,
  Search,
  User,
  Home,
  Plus,
  Clock,
  MapPin,
  Eye,
  Edit,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Upload,
  Image as ImageIcon,
  X as XIcon,
  Mail,
  Copy,
  CheckCircle,
  AlertTriangle,
  UserMinus,
  UserX,
  Send,
  Briefcase,
  Download,
  FileCheck,
  ArrowLeft,
  ToggleLeft,
  ToggleRight,
  Star
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

const Dashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [activeTab, setActiveTab] = useState('profile');
  const { user, member, isLoading, logout, hasPermission, canEdit } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      console.log('Logout button clicked');
      await logout();
      console.log('Logout completed, redirecting to home');
      // Use window.location to force a complete page refresh and avoid any React state issues
      window.location.href = '/';
    } catch (error) {
      console.error('Logout error:', error);
      alert('Logout failed. Please try again.');
    }
  };

  const navigationItems = [
    {
      id: 'profile',
      name: 'My Profile',
      icon: User,
      requiredRole: 'member',
      description: 'View and edit your personal information'
    },
    {
      id: 'events',
      name: 'Manage Events',
      icon: Calendar,
      requiredRole: 'member',
      description: 'Create and manage DMLS events'
    },
    {
      id: 'articles',
      name: 'Article of the Week',
      icon: FileText,
      requiredRole: 'executive',
      description: 'Manage weekly featured articles'
    },
    {
      id: 'careers',
      name: 'Careers',
      icon: Briefcase,
      requiredRole: 'executive',
      description: 'Post roles and review applications'
    },
    {
      id: 'team',
      name: 'Team Management',
      icon: Users,
      requiredRole: 'admin',
      description: 'Manage council members and roles'
    },
    {
      id: 'settings',
      name: 'Settings',
      icon: Settings,
      requiredRole: 'member',
      description: 'System and account settings'
    }
  ];

  const filteredNavigation = navigationItems.filter(item => 
    hasPermission(item.requiredRole)
  );

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-red-500';
      case 'executive': return 'bg-blue-500';
      case 'member': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  const getUserName = () => {
    if (member?.first_name && member?.last_name) {
      return `${member.first_name} ${member.last_name}`;
    }
    return user?.email?.split('@')[0] || 'User';
  };

  const getUserInitials = () => {
    if (member?.first_name && member?.last_name) {
      return `${member.first_name[0]}${member.last_name[0]}`;
    }
    const email = user?.email || '';
    return email[0]?.toUpperCase() || 'U';
  };

  const getUserRole = () => {
    if (member?.member_roles && member.member_roles.length > 0) {
      // Get the highest level role
      const roles = member.member_roles.filter(mr => !mr.end_date);
      if (roles.length > 0) {
        const highestRole = roles.reduce((prev, current) => 
          (current.roles.level > prev.roles.level) ? current : prev
        );
        return highestRole.roles.name;
      }
    }
    return 'member'; // default role
  };

  // Show loading state while authentication is being determined
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            className="w-12 h-12 border-4 border-[#0395fc] border-t-transparent rounded-full mx-auto mb-4"
          />
          <p className="text-gray-600 font-[var(--font-dm-sans)]">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <AnimatePresence>
        {(sidebarOpen || window.innerWidth >= 1024) && (
          <motion.aside
            initial={{ x: -300 }}
            animate={{ x: 0 }}
            exit={{ x: -300 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r-4 border-black shadow-[4px_0px_0px_0px_rgba(0,0,0,1)] flex flex-col"
          >
            {/* Sidebar Header */}
            <div className="flex items-center justify-between p-6 border-b-2 border-black">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#0395fc] rounded-full flex items-center justify-center border-2 border-black">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="font-black text-lg font-[var(--font-anton)]">DMLS</h2>
                  <p className="text-xs text-muted-foreground font-[var(--font-dm-sans)]">Dashboard</p>
                </div>
              </div>
              <button
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden p-2 hover:bg-gray-100 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* User Info */}
                        {/* User Info */}
            <div className="px-6 py-4 border-t-2 border-black bg-gray-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#0395fc] rounded-full flex items-center justify-center border-2 border-black text-white font-bold">
                  <span className="text-sm">
                    {getUserInitials()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-gray-900 truncate font-[var(--font-dm-sans)]">
                    {getUserName()}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`w-2 h-2 rounded-full ${getRoleColor(getUserRole())}`}></span>
                    <span className="text-xs text-gray-500 capitalize font-[var(--font-dm-sans)]">
                      {getUserRole()}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <nav className="flex-1 p-6">
              <div className="space-y-2">
                {filteredNavigation.map((item) => {
                  const Icon = item.icon;
                  const isActive = activeTab === item.id;
                  
                  return (
                    <button
                      key={item.id}
                      onClick={() => setActiveTab(item.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border-2 transition-all duration-200 font-[var(--font-dm-sans)] ${
                        isActive
                          ? 'bg-[#0395fc] text-white border-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]'
                          : 'bg-white text-foreground border-gray-200 hover:border-black hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="font-medium">{item.name}</span>
                    </button>
                  );
                })}
              </div>
            </nav>

            {/* Sidebar Footer */}
            <div className="p-6 border-t-2 border-black">
              <button
                onClick={() => navigate('/')}
                className="w-full flex items-center gap-3 px-4 py-3 mb-3 text-left bg-gray-100 border-2 border-gray-300 rounded-lg hover:border-black hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all duration-200"
              >
                <Home className="w-5 h-5" />
                <span className="font-medium font-[var(--font-dm-sans)]">Back to Website</span>
              </button>
              
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-3 text-left bg-red-50 text-red-600 border-2 border-red-200 rounded-lg hover:border-red-500 hover:shadow-[2px_2px_0px_0px_rgba(239,68,68,1)] transition-all duration-200"
              >
                <LogOut className="w-5 h-5" />
                <span className="font-medium font-[var(--font-dm-sans)]">Logout</span>
              </button>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="bg-white border-b-4 border-black shadow-[0px_4px_0px_0px_rgba(0,0,0,1)] p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 border-2 border-black rounded hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all duration-200"
              >
                <Menu className="w-5 h-5" />
              </button>
              
              <div>
                <h1 className="text-2xl font-black font-[var(--font-anton)] text-foreground">
                  {filteredNavigation.find(item => item.id === activeTab)?.name || 'Dashboard'}
                </h1>
                <p className="text-muted-foreground font-[var(--font-dm-sans)]">
                  Welcome back, {getUserName()}!
                </p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              {/* Search */}
              <div className="relative hidden md:block">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search..."
                  className="pl-10 pr-4 py-2 border-2 border-black rounded font-[var(--font-dm-sans)] focus:outline-none focus:ring-0 focus:border-[#0395fc] transition-colors duration-200"
                />
              </div>

              {/* Notifications */}
              <button className="relative p-2 border-2 border-black rounded hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all duration-200">
                <Bell className="w-5 h-5" />
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 border border-white rounded-full"></span>
              </button>
            </div>
          </div>
        </header>

        {/* Dashboard Content */}
        <main className="flex-1 p-6">
          <DashboardContent activeTab={activeTab} user={user} member={member} />
        </main>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setSidebarOpen(false)}
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
        />
      )}
    </div>
  );
};

const DashboardContent = ({ activeTab, user, member }) => {
  const { canEdit } = useAuth();

  const renderContent = () => {
    switch (activeTab) {
      case 'profile':
        return <ProfileTab user={user} member={member} />;
      case 'events':
        return <EventsTab canEdit={canEdit} member={member} user={user} />;
      case 'careers':
        return <CareersTab />;
      case 'team':
        return <TeamTab canEdit={canEdit} />;
      case 'articles':
        return <ArticlesTab canEdit={canEdit} member={member} />;
      case 'settings':
        return <SettingsTab user={user} member={member} />;
      default:
        return <ProfileTab user={user} member={member} />;
    }
  };

  return (
    <motion.div
      key={activeTab}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {renderContent()}
    </motion.div>
  );
};

const ProfileTab = ({ user, member }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [profileData, setProfileData] = useState({
    firstName: member?.first_name || '',
    lastName: member?.last_name || '',
    studentId: member?.student_id || '',
    program: member?.program || '',
    graduationYear: member?.graduation_year || '',
    bio: member?.bio || '',
    linkedinUrl: member?.linkedin_url || '',
    githubUrl: member?.github_url || '',
    websiteUrl: member?.website_url || ''
  });

  const getUserRole = () => {
    if (member?.member_roles && member.member_roles.length > 0) {
      const roles = member.member_roles.filter(mr => !mr.end_date);
      if (roles.length > 0) {
        const highestRole = roles.reduce((prev, current) => 
          (current.roles.level > prev.roles.level) ? current : prev
        );
        return highestRole.roles.name;
      }
    }
    return 'member';
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    // TODO: Implement save to database
    console.log('Saving profile data:', profileData);
    setIsEditing(false);
    // Here we'll integrate with the updateMemberProfile function
  };

  const handleCancel = () => {
    // Reset to original data
    setProfileData({
      firstName: member?.first_name || '',
      lastName: member?.last_name || '',
      studentId: member?.student_id || '',
      program: member?.program || '',
      graduationYear: member?.graduation_year || '',
      bio: member?.bio || '',
      linkedinUrl: member?.linkedin_url || '',
      githubUrl: member?.github_url || '',
      websiteUrl: member?.website_url || ''
    });
    setIsEditing(false);
  };

  return (
    <div className="space-y-8">
      {/* Profile Header */}
      <div className="bg-gradient-to-r from-[#0395fc] to-[#0070c0] text-white p-8 rounded-lg border-4 border-black shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-3xl font-black font-[var(--font-anton)] mb-2">
              My Profile
            </h2>
            <p className="text-white/90 font-[var(--font-dm-sans)]">
              Council Role: <span className="font-bold capitalize">{getUserRole()}</span>
            </p>
            <p className="text-white/80 text-sm font-[var(--font-dm-sans)] mt-1">
              {user?.email}
            </p>
          </div>
          <button
            onClick={() => setIsEditing(!isEditing)}
            className="bg-white text-[#0395fc] px-4 py-2 rounded border-2 border-black font-bold font-[var(--font-dm-sans)] shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:shadow-[1px_1px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[1px] hover:translate-y-[1px] transition-all duration-200"
          >
            {isEditing ? 'Cancel' : 'Edit Profile'}
          </button>
        </div>
      </div>

      {/* Profile Information */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Basic Information */}
        <div className="bg-white p-6 rounded-lg border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
          <h3 className="text-xl font-black font-[var(--font-anton)] mb-6 flex items-center gap-2">
            <User className="w-5 h-5" />
            Basic Information
          </h3>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">First Name</label>
                {isEditing ? (
                  <input
                    type="text"
                    name="firstName"
                    value={profileData.firstName}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded focus:border-[#0395fc] focus:outline-none"
                  />
                ) : (
                  <p className="text-gray-900 font-[var(--font-dm-sans)]">
                    {profileData.firstName || 'Not provided'}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Last Name</label>
                {isEditing ? (
                  <input
                    type="text"
                    name="lastName"
                    value={profileData.lastName}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded focus:border-[#0395fc] focus:outline-none"
                  />
                ) : (
                  <p className="text-gray-900 font-[var(--font-dm-sans)]">
                    {profileData.lastName || 'Not provided'}
                  </p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Student ID</label>
              {isEditing ? (
                <input
                  type="text"
                  name="studentId"
                  value={profileData.studentId}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded focus:border-[#0395fc] focus:outline-none"
                />
              ) : (
                <p className="text-gray-900 font-[var(--font-dm-sans)]">
                  {profileData.studentId || 'Not provided'}
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Program</label>
                {isEditing ? (
                  <input
                    type="text"
                    name="program"
                    value={profileData.program}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded focus:border-[#0395fc] focus:outline-none"
                  />
                ) : (
                  <p className="text-gray-900 font-[var(--font-dm-sans)]">
                    {profileData.program || 'Not provided'}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Graduation Year</label>
                {isEditing ? (
                  <input
                    type="number"
                    name="graduationYear"
                    value={profileData.graduationYear}
                    onChange={handleInputChange}
                    min="2024"
                    max="2030"
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded focus:border-[#0395fc] focus:outline-none"
                  />
                ) : (
                  <p className="text-gray-900 font-[var(--font-dm-sans)]">
                    {profileData.graduationYear || 'Not provided'}
                  </p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Bio</label>
              {isEditing ? (
                <textarea
                  name="bio"
                  value={profileData.bio}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded focus:border-[#0395fc] focus:outline-none"
                  placeholder="Tell us about yourself..."
                />
              ) : (
                <p className="text-gray-900 font-[var(--font-dm-sans)]">
                  {profileData.bio || 'No bio provided yet.'}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Social Links */}
        <div className="bg-white p-6 rounded-lg border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
          <h3 className="text-xl font-black font-[var(--font-anton)] mb-6">Social Links</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">LinkedIn</label>
              {isEditing ? (
                <input
                  type="url"
                  name="linkedinUrl"
                  value={profileData.linkedinUrl}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded focus:border-[#0395fc] focus:outline-none"
                  placeholder="https://linkedin.com/in/username"
                />
              ) : (
                <p className="text-gray-900 font-[var(--font-dm-sans)]">
                  {profileData.linkedinUrl ? (
                    <a href={profileData.linkedinUrl} target="_blank" rel="noopener noreferrer" className="text-[#0395fc] hover:underline">
                      {profileData.linkedinUrl}
                    </a>
                  ) : (
                    'Not provided'
                  )}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">GitHub</label>
              {isEditing ? (
                <input
                  type="url"
                  name="githubUrl"
                  value={profileData.githubUrl}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded focus:border-[#0395fc] focus:outline-none"
                  placeholder="https://github.com/username"
                />
              ) : (
                <p className="text-gray-900 font-[var(--font-dm-sans)]">
                  {profileData.githubUrl ? (
                    <a href={profileData.githubUrl} target="_blank" rel="noopener noreferrer" className="text-[#0395fc] hover:underline">
                      {profileData.githubUrl}
                    </a>
                  ) : (
                    'Not provided'
                  )}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Website</label>
              {isEditing ? (
                <input
                  type="url"
                  name="websiteUrl"
                  value={profileData.websiteUrl}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded focus:border-[#0395fc] focus:outline-none"
                  placeholder="https://yourwebsite.com"
                />
              ) : (
                <p className="text-gray-900 font-[var(--font-dm-sans)]">
                  {profileData.websiteUrl ? (
                    <a href={profileData.websiteUrl} target="_blank" rel="noopener noreferrer" className="text-[#0395fc] hover:underline">
                      {profileData.websiteUrl}
                    </a>
                  ) : (
                    'Not provided'
                  )}
                </p>
              )}
            </div>
          </div>

          {isEditing && (
            <div className="flex gap-3 mt-6">
              <button
                onClick={handleSave}
                className="bg-[#0395fc] text-white px-6 py-2 rounded border-2 border-black font-bold font-[var(--font-dm-sans)] shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:shadow-[1px_1px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[1px] hover:translate-y-[1px] transition-all duration-200"
              >
                Save Changes
              </button>
              <button
                onClick={handleCancel}
                className="bg-gray-500 text-white px-6 py-2 rounded border-2 border-black font-bold font-[var(--font-dm-sans)] shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:shadow-[1px_1px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[1px] hover:translate-y-[1px] transition-all duration-200"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>

      {!member && (
        <div className="bg-yellow-50 border-2 border-yellow-400 p-6 rounded-lg">
          <h3 className="text-lg font-bold text-yellow-800 mb-2">Complete Your Profile</h3>
          <p className="text-yellow-700 font-[var(--font-dm-sans)]">
            Fill out your profile information to access all dashboard features and connect with other members.
          </p>
        </div>
      )}
    </div>
  );
};





// Events Management Tab Component
const EventsTab = ({ canEdit, member, user }) => {
  const [events, setEvents] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingEvents, setIsLoadingEvents] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalEvents, setTotalEvents] = useState(0);
  const eventsPerPage = 6;
  const [eventForm, setEventForm] = useState({
    title: '',
    description: '',
    content: '',
    event_type: 'workshop',
    location: '',
    location_type: 'in-person',
    virtual_link: '',
    start_datetime: '',
    end_datetime: '',
    registration_required: false,
    max_attendees: '',
    tags: ''
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  const eventTypes = [
    { value: 'workshop', label: 'Workshop' },
    { value: 'seminar', label: 'Seminar' },
    { value: 'hackathon', label: 'Hackathon' },
    { value: 'social', label: 'Social Event' },
    { value: 'meeting', label: 'Meeting' }
  ];

  // Fetch events from database
  const fetchEvents = async () => {
    try {
      setIsLoadingEvents(true);

      // Calculate offset for pagination
      const from = (currentPage - 1) * eventsPerPage;
      const to = from + eventsPerPage - 1;

      // Fetch events with pagination
      const { data: eventsData, error: fetchError, count } = await supabase
        .from('events')
        .select(`
          *,
          members!events_created_by_fkey (
            first_name,
            last_name,
            profile_image_url
          )
        `, { count: 'exact' })
        .order('created_at', { ascending: false })
        .range(from, to);

      if (fetchError) {
        console.error('Error fetching events:', fetchError);
        return;
      }

      setEvents(eventsData || []);
      setTotalEvents(count || 0);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setIsLoadingEvents(false);
    }
  };

  // Fetch events on component mount and when page changes
  useEffect(() => {
    fetchEvents();
  }, [currentPage]);

  // Edit event (placeholder - could be expanded to full edit form)
  const handleEditEvent = (event) => {
    // For now, just populate the form with existing data
    setEventForm({
      title: event.title,
      description: event.description || '',
      content: event.content || '',
      event_type: event.event_type,
      location: event.location || '',
      location_type: event.location_type,
      virtual_link: event.virtual_link || '',
      start_datetime: event.start_datetime ? new Date(event.start_datetime).toISOString().slice(0, 16) : '',
      end_datetime: event.end_datetime ? new Date(event.end_datetime).toISOString().slice(0, 16) : '',
      registration_required: event.registration_required || false,
      max_attendees: event.max_attendees || '',
      tags: event.tags ? event.tags.join(', ') : ''
    });
    setShowCreateForm(true);
  };

  // Delete event
  const handleDeleteEvent = async (eventId, eventTitle) => {
    if (!window.confirm(`Are you sure you want to delete "${eventTitle}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('events')
        .delete()
        .eq('id', eventId);

      if (error) {
        console.error('Error deleting event:', error);
        alert('Failed to delete event. Please try again.');
        return;
      }

      // Refresh events list
      fetchEvents();
      alert('Event deleted successfully.');
    } catch (error) {
      console.error('Error deleting event:', error);
      alert('Failed to delete event. Please try again.');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEventForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle image selection
  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file.');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size must be less than 5MB.');
        return;
      }
      
      setSelectedImage(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  // Remove selected image
  const removeSelectedImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
  };

  // Upload image to Supabase storage
  const uploadEventImage = async (file, eventSlug) => {
    try {
      setIsUploadingImage(true);
      
      const fileExt = file.name.split('.').pop();
      const fileName = `${eventSlug}-${Date.now()}.${fileExt}`;
      const filePath = `events/${fileName}`;

      const { data, error } = await supabase.storage
        .from('event-images')
        .upload(filePath, file);

      if (error) throw error;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('event-images')
        .getPublicUrl(filePath);

      return publicUrl;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      console.log('Creating event:', eventForm);
      
      // Generate a slug from the title
      const slug = eventForm.title
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim('-');

      // Parse tags
      const tagsArray = eventForm.tags 
        ? eventForm.tags.split(',').map(tag => tag.trim()).filter(tag => tag)
        : [];

      // Check if user has member profile
      if (!member?.id) {
        throw new Error('You need to complete your profile before creating events.');
      }

      let eventImageUrl = null;

      // Upload image if selected
      if (selectedImage) {
        try {
          eventImageUrl = await uploadEventImage(selectedImage, slug);
        } catch (imageError) {
          console.error('Image upload failed:', imageError);
          // Continue without image, don't fail the whole submission
        }
      }

      // Prepare event data for database
      const eventData = {
        title: eventForm.title,
        slug: slug,
        description: eventForm.description || null,
        content: eventForm.content || null,
        event_type: eventForm.event_type,
        location: eventForm.location_type === 'virtual' ? null : eventForm.location,
        location_type: eventForm.location_type,
        virtual_link: eventForm.location_type === 'virtual' ? eventForm.virtual_link : null,
        start_datetime: eventForm.start_datetime,
        end_datetime: eventForm.end_datetime,
        registration_required: eventForm.registration_required,
        max_attendees: eventForm.max_attendees ? parseInt(eventForm.max_attendees) : null,
        tags: tagsArray,
        image_url: eventImageUrl,
        status: 'published', // Automatically publish events
        is_featured: false,
        is_public: true,
        created_by: member.id, // Use member ID as required by schema
        current_attendees: 0
      };

      console.log('Saving event data:', eventData);

      // Save to database
      const { data, error } = await supabase
        .from('events')
        .insert([eventData])
        .select();

      if (error) {
        console.error('Database error:', error);
        throw error;
      }

      console.log('Event created successfully:', data);
      
      // Reset form
      setEventForm({
        title: '',
        description: '',
        content: '',
        event_type: 'workshop',
        location: '',
        location_type: 'in-person',
        virtual_link: '',
        start_datetime: '',
        end_datetime: '',
        registration_required: false,
        max_attendees: '',
        tags: ''
      });
      
      setSelectedImage(null);
      setImagePreview(null);
      
      // Refresh events list
      fetchEvents();
      
      alert('Event created successfully and is now live on the website!');
    } catch (error) {
      console.error('Error creating event:', error);
      alert('Failed to create event. Please try again. Error: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-black font-[var(--font-anton)] text-foreground">
            Event Management
          </h2>
          <p className="text-muted-foreground font-[var(--font-dm-sans)] mt-1">
            Create and manage DMLS events and workshops
          </p>
        </div>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="bg-[#0395fc] text-white px-6 py-3 rounded border-2 border-black font-bold font-[var(--font-dm-sans)] shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] transition-all duration-200 flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          {showCreateForm ? 'Cancel' : 'Create Event'}
        </button>
      </div>

      {/* Create Event Form */}
      {showCreateForm && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="bg-white p-8 rounded-lg border-2 border-black shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]"
        >
          <h3 className="text-xl font-black font-[var(--font-anton)] mb-6">Create New Event</h3>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Title */}
              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-gray-700 mb-2">Event Title *</label>
                <input
                  type="text"
                  name="title"
                  value={eventForm.title}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded focus:border-[#0395fc] focus:outline-none font-[var(--font-dm-sans)]"
                  placeholder="Enter event title"
                />
              </div>

              {/* Event Type */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Event Type *</label>
                <select
                  name="event_type"
                  value={eventForm.event_type}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded focus:border-[#0395fc] focus:outline-none font-[var(--font-dm-sans)]"
                >
                  {eventTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              {/* Location Type */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Location Type *</label>
                <select
                  name="location_type"
                  value={eventForm.location_type}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded focus:border-[#0395fc] focus:outline-none font-[var(--font-dm-sans)]"
                >
                  <option value="in-person">In-Person</option>
                  <option value="virtual">Virtual</option>
                  <option value="hybrid">Hybrid</option>
                </select>
              </div>

              {/* Location */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  {eventForm.location_type === 'virtual' ? 'Meeting Link' : 'Location'} *
                </label>
                <input
                  type={eventForm.location_type === 'virtual' ? 'url' : 'text'}
                  name={eventForm.location_type === 'virtual' ? 'virtual_link' : 'location'}
                  value={eventForm.location_type === 'virtual' ? eventForm.virtual_link : eventForm.location}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded focus:border-[#0395fc] focus:outline-none font-[var(--font-dm-sans)]"
                  placeholder={eventForm.location_type === 'virtual' ? 'https://zoom.us/...' : 'Enter location'}
                />
              </div>

              {/* Start DateTime */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Start Date & Time *</label>
                <input
                  type="datetime-local"
                  name="start_datetime"
                  value={eventForm.start_datetime}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded focus:border-[#0395fc] focus:outline-none font-[var(--font-dm-sans)]"
                />
              </div>

              {/* End DateTime */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">End Date & Time *</label>
                <input
                  type="datetime-local"
                  name="end_datetime"
                  value={eventForm.end_datetime}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded focus:border-[#0395fc] focus:outline-none font-[var(--font-dm-sans)]"
                />
              </div>

              {/* Max Attendees */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Max Attendees</label>
                <input
                  type="number"
                  name="max_attendees"
                  value={eventForm.max_attendees}
                  onChange={handleInputChange}
                  min="1"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded focus:border-[#0395fc] focus:outline-none font-[var(--font-dm-sans)]"
                  placeholder="Leave empty for unlimited"
                />
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Tags</label>
                <input
                  type="text"
                  name="tags"
                  value={eventForm.tags}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded focus:border-[#0395fc] focus:outline-none font-[var(--font-dm-sans)]"
                  placeholder="e.g., python, machine-learning, beginner (comma separated)"
                />
              </div>

              {/* Description */}
              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-gray-700 mb-2">Short Description</label>
                <textarea
                  name="description"
                  value={eventForm.description}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded focus:border-[#0395fc] focus:outline-none font-[var(--font-dm-sans)]"
                  placeholder="Brief description of the event"
                />
              </div>

              {/* Content */}
              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-gray-700 mb-2">Full Description</label>
                <textarea
                  name="content"
                  value={eventForm.content}
                  onChange={handleInputChange}
                  rows={6}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded focus:border-[#0395fc] focus:outline-none font-[var(--font-dm-sans)]"
                  placeholder="Detailed event description, agenda, requirements, etc."
                />
              </div>

              {/* Image Upload */}
              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-gray-700 mb-3">Event Image (Optional)</label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  {selectedImage ? (
                    <div className="space-y-4">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="max-w-full max-h-48 mx-auto rounded border-2 border-gray-200"
                      />
                      <div className="flex justify-center gap-4">
                        <button
                          type="button"
                          onClick={removeSelectedImage}
                          className="bg-red-500 text-white px-4 py-2 rounded border-2 border-black font-bold font-[var(--font-dm-sans)] shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:shadow-[1px_1px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[1px] hover:translate-y-[1px] transition-all duration-200 text-sm"
                        >
                          Remove Image
                        </button>
                      </div>
                      <p className="text-sm text-gray-600 font-[var(--font-dm-sans)]">
                        {selectedImage.name} ({(selectedImage.size / 1024 / 1024).toFixed(2)} MB)
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <ImageIcon className="w-12 h-12 text-gray-400 mx-auto" />
                      <div>
                        <label htmlFor="event-image-upload" className="cursor-pointer">
                          <span className="bg-[#0395fc] text-white px-6 py-2 rounded border-2 border-black font-bold font-[var(--font-dm-sans)] shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[1px] hover:translate-y-[1px] transition-all duration-200 inline-block">
                            Choose Image
                          </span>
                          <input
                            id="event-image-upload"
                            type="file"
                            accept="image/*"
                            onChange={handleImageSelect}
                            className="hidden"
                          />
                        </label>
                      </div>
                      <p className="text-sm text-gray-600 font-[var(--font-dm-sans)]">
                        PNG, JPG, GIF up to 5MB
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Registration Required */}
              <div className="md:col-span-2">
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    name="registration_required"
                    checked={eventForm.registration_required}
                    onChange={handleInputChange}
                    className="w-5 h-5 text-[#0395fc] border-2 border-gray-300 rounded focus:ring-[#0395fc]"
                  />
                  <span className="font-bold text-gray-700">Require registration for this event</span>
                </label>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex gap-4 pt-4">
              <button
                type="submit"
                disabled={isLoading}
                className="bg-[#0395fc] text-white px-8 py-3 rounded border-2 border-black font-bold font-[var(--font-dm-sans)] shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Creating...' : 'Create Event'}
              </button>
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="bg-gray-500 text-white px-8 py-3 rounded border-2 border-black font-bold font-[var(--font-dm-sans)] shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] transition-all duration-200"
              >
                Cancel
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Events List */}
      <div className="bg-white p-6 rounded-lg border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-black font-[var(--font-anton)]">Events ({totalEvents})</h3>
          <div className="text-sm text-gray-600">
            Page {currentPage} of {Math.ceil(totalEvents / eventsPerPage)}
          </div>
        </div>

        {/* Loading State */}
        {isLoadingEvents ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#0395fc] mx-auto mb-4" />
            <p className="text-gray-600">Loading events...</p>
          </div>
        ) : events.length === 0 ? (
          /* Empty State */
          <div className="text-center py-12">
            <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h4 className="text-lg font-bold text-gray-600 mb-2">No events yet</h4>
            <p className="text-gray-500 font-[var(--font-dm-sans)]">
              Create your first event to get started with event management.
            </p>
          </div>
        ) : (
          /* Events Grid */
          <div className="space-y-4">
            {events.map((event) => (
              <div 
                key={event.id} 
                className="bg-gray-50 border-2 border-gray-200 rounded-lg p-4 hover:border-[#0395fc] transition-colors"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="bg-[#0395fc] text-white px-2 py-1 text-xs font-bold rounded">
                        {event.event_type?.toUpperCase() || 'EVENT'}
                      </span>
                      <span className="bg-green-100 text-green-800 px-2 py-1 text-xs font-medium rounded">
                        {event.status?.toUpperCase() || 'PUBLISHED'}
                      </span>
                    </div>
                    
                    <h4 className="text-lg font-bold text-gray-900 mb-2">{event.title}</h4>
                    
                    {event.description && (
                      <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                        {event.description.length > 150 
                          ? `${event.description.substring(0, 150)}...` 
                          : event.description}
                      </p>
                    )}
                    
                    <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        <span>
                          {event.start_datetime 
                            ? new Date(event.start_datetime).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })
                            : 'Date TBA'
                          }
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        <span>
                          {event.start_datetime 
                            ? new Date(event.start_datetime).toLocaleTimeString('en-US', {
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true
                              })
                            : 'Time TBA'
                          }
                        </span>
                      </div>
                      
                      {event.location && (
                        <div className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          <span>{event.location}</span>
                        </div>
                      )}
                      
                      {event.current_attendees !== undefined && (
                        <div className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          <span>{event.current_attendees} registered</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => window.open(`/events/${event.slug}`, '_blank')}
                      className="bg-blue-100 text-blue-700 p-2 rounded border border-blue-300 hover:bg-blue-200 transition-colors"
                      title="View Event"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    {canEdit && (
                      <>
                        <button
                          onClick={() => handleEditEvent(event)}
                          className="bg-yellow-100 text-yellow-700 p-2 rounded border border-yellow-300 hover:bg-yellow-200 transition-colors"
                          title="Edit Event"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteEvent(event.id, event.title)}
                          className="bg-red-100 text-red-700 p-2 rounded border border-red-300 hover:bg-red-200 transition-colors"
                          title="Delete Event"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {!isLoadingEvents && totalEvents > eventsPerPage && (
          <div className="flex justify-center items-center gap-4 mt-8">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 border-2 border-black rounded font-bold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200 transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </button>
            
            <div className="flex gap-2">
              {[...Array(Math.ceil(totalEvents / eventsPerPage))].map((_, index) => {
                const pageNumber = index + 1;
                const isCurrentPage = pageNumber === currentPage;
                
                return (
                  <button
                    key={pageNumber}
                    onClick={() => setCurrentPage(pageNumber)}
                    className={`w-10 h-10 rounded border-2 border-black font-bold transition-colors ${
                      isCurrentPage
                        ? 'bg-[#0395fc] text-white'
                        : 'bg-white text-black hover:bg-gray-100'
                    }`}
                  >
                    {pageNumber}
                  </button>
                );
              })}
            </div>
            
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(totalEvents / eventsPerPage)))}
              disabled={currentPage === Math.ceil(totalEvents / eventsPerPage)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 border-2 border-black rounded font-bold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200 transition-colors"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Articles Management Tab Component
const ArticlesTab = ({ canEdit, member }) => {
  const [articles, setArticles] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingArticles, setIsLoadingArticles] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalArticles, setTotalArticles] = useState(0);
  const articlesPerPage = 6;
  const [articleForm, setArticleForm] = useState({
    title: '',
    excerpt: '',
    content: '',
    article_type: 'article_of_week',
    external_url: '',
    category: '',
    tags: '',
    is_featured: false
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [imageError, setImageError] = useState(null);

  const articleTypes = [
    { value: 'article_of_week', label: 'Article of the Week' },
    { value: 'blog', label: 'Blog Post' },
    { value: 'tutorial', label: 'Tutorial' },
    { value: 'news', label: 'News' }
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setArticleForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Fetch articles from database
  const fetchArticles = async () => {
    try {
      setIsLoadingArticles(true);

      // Calculate offset for pagination
      const from = (currentPage - 1) * articlesPerPage;
      const to = from + articlesPerPage - 1;

      // Fetch articles with pagination
      const { data: articlesData, error: fetchError, count } = await supabase
        .from('articles')
        .select(`
          *,
          members!articles_author_id_fkey (
            first_name,
            last_name,
            profile_image_url
          )
        `, { count: 'exact' })
        .order('created_at', { ascending: false })
        .range(from, to);

      if (fetchError) {
        console.error('Error fetching articles:', fetchError);
        return;
      }

      setArticles(articlesData || []);
      setTotalArticles(count || 0);
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setIsLoadingArticles(false);
    }
  };

  // Fetch articles on component mount and when page changes
  useEffect(() => {
    fetchArticles();
  }, [currentPage]);

  // Handle image selection
  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file.');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size must be less than 5MB.');
        return;
      }
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setSelectedImage({
          file: file,
          preview: e.target.result
        });
      };
      reader.readAsDataURL(file);
    }
  };

  // Remove selected image
  const removeSelectedImage = () => {
    setSelectedImage(null);
  };

  // Upload image to Supabase storage
  const uploadArticleImage = async (file, articleSlug) => {
    try {
      setIsUploadingImage(true);
      
      const fileExt = file.name.split('.').pop();
      const fileName = `${articleSlug}-${Date.now()}.${fileExt}`;
      const filePath = `articles/${fileName}`;

      const { data, error } = await supabase.storage
        .from('article-images')
        .upload(filePath, file);

      if (error) throw error;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('article-images')
        .getPublicUrl(filePath);

      return publicUrl;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      console.log('Creating article:', articleForm);
      
      // Generate a slug from the title
      const slug = articleForm.title
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim('-');

      // Parse tags
      const tagsArray = articleForm.tags 
        ? articleForm.tags.split(',').map(tag => tag.trim()).filter(tag => tag)
        : [];

      // Check if user has member profile
      if (!member?.id) {
        throw new Error('You need to complete your profile before creating articles.');
      }

      let featuredImageUrl = null;

      // Upload image if selected
      if (selectedImage) {
        try {
          featuredImageUrl = await uploadArticleImage(selectedImage.file, slug);
        } catch (imageError) {
          console.error('Image upload failed:', imageError);
          // Continue without image, don't fail the whole submission
        }
      }

      // Prepare article data for database
      const articleData = {
        title: articleForm.title,
        slug: slug,
        excerpt: articleForm.excerpt || null,
        content: articleForm.content,
        article_type: articleForm.article_type,
        reading_time: articleForm.content ? Math.ceil(articleForm.content.split(' ').length / 200) : 5, // Rough estimate
        external_url: articleForm.external_url || null,
        featured_image_url: featuredImageUrl,
        tags: tagsArray,
        category: articleForm.category || null,
        status: 'published', // Automatically publish articles
        published_at: new Date().toISOString(),
        is_featured: articleForm.is_featured,
        author_id: member.id, // Use member ID as required by schema
        view_count: 0,
        like_count: 0
      };

      console.log('Saving article data:', articleData);

      // Save to database
      const { data, error } = await supabase
        .from('articles')
        .insert([articleData])
        .select();

      if (error) {
        console.error('Database error:', error);
        throw error;
      }

      console.log('Article created successfully:', data);
      
      // Reset form
      setArticleForm({
        title: '',
        excerpt: '',
        content: '',
        article_type: 'article_of_week',
        external_url: '',
        category: '',
        tags: '',
        is_featured: false
      });
      
      setSelectedImage(null);
      
      setShowCreateForm(false);
      
      // Refresh articles list
      fetchArticles();
      
      alert('Article created successfully and is now live on the website!');
    } catch (error) {
      console.error('Error creating article:', error);
      alert('Failed to create article. Please try again. Error: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Delete article function
  const handleDeleteArticle = async (articleId, articleTitle) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Are you sure you want to delete the article "${articleTitle}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setIsLoading(true);

      const { error } = await supabase
        .from('articles')
        .delete()
        .eq('id', articleId);

      if (error) {
        console.error('Error deleting article:', error);
        throw error;
      }

      // Refresh articles list
      fetchArticles();
      
      alert('Article deleted successfully!');
    } catch (error) {
      console.error('Error deleting article:', error);
      alert('Failed to delete article. Please try again. Error: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-black font-[var(--font-anton)] text-foreground">
            Article Management
          </h2>
          <p className="text-muted-foreground font-[var(--font-dm-sans)] mt-1">
            Create and manage articles and weekly features
          </p>
        </div>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="bg-[#0395fc] text-white px-6 py-3 rounded border-2 border-black font-bold font-[var(--font-dm-sans)] shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] transition-all duration-200 flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          {showCreateForm ? 'Cancel' : 'Create Article'}
        </button>
      </div>

      {/* Create Article Form */}
      {showCreateForm && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="bg-white p-8 rounded-lg border-2 border-black shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]"
        >
          <h3 className="text-xl font-black font-[var(--font-anton)] mb-6">Create New Article</h3>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Title */}
              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-gray-700 mb-2">Article Title *</label>
                <input
                  type="text"
                  name="title"
                  value={articleForm.title}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded focus:border-[#0395fc] focus:outline-none font-[var(--font-dm-sans)]"
                  placeholder="Enter article title"
                />
              </div>

              {/* Article Type */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Article Type *</label>
                <select
                  name="article_type"
                  value={articleForm.article_type}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded focus:border-[#0395fc] focus:outline-none font-[var(--font-dm-sans)]"
                >
                  {articleTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Category</label>
                <input
                  type="text"
                  name="category"
                  value={articleForm.category}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded focus:border-[#0395fc] focus:outline-none font-[var(--font-dm-sans)]"
                  placeholder="e.g., Machine Learning, Web Development"
                />
              </div>

              {/* External URL (for Article of the Week) */}
              {articleForm.article_type === 'article_of_week' && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-bold text-gray-700 mb-2">External Article URL</label>
                  <input
                    type="url"
                    name="external_url"
                    value={articleForm.external_url}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded focus:border-[#0395fc] focus:outline-none font-[var(--font-dm-sans)]"
                    placeholder="https://example.com/article"
                  />
                  <p className="text-sm text-gray-600 mt-1">Link to the original article (optional)</p>
                </div>
              )}

              {/* Tags */}
              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-gray-700 mb-2">Tags</label>
                <input
                  type="text"
                  name="tags"
                  value={articleForm.tags}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded focus:border-[#0395fc] focus:outline-none font-[var(--font-dm-sans)]"
                  placeholder="e.g., python, machine-learning, tutorial (comma separated)"
                />
              </div>

              {/* Excerpt */}
              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-gray-700 mb-2">Short Excerpt</label>
                <textarea
                  name="excerpt"
                  value={articleForm.excerpt}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded focus:border-[#0395fc] focus:outline-none font-[var(--font-dm-sans)]"
                  placeholder="Brief summary of the article"
                />
              </div>

              {/* Content */}
              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-gray-700 mb-2">Full Content</label>
                <textarea
                  name="content"
                  value={articleForm.content}
                  onChange={handleInputChange}
                  rows={8}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded focus:border-[#0395fc] focus:outline-none font-[var(--font-dm-sans)]"
                  placeholder="Full article content (supports Markdown)"
                />
                <p className="text-sm text-gray-600 mt-1">You can use Markdown formatting</p>
              </div>

              {/* Featured Article */}
              <div className="md:col-span-2">
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    name="is_featured"
                    checked={articleForm.is_featured}
                    onChange={handleInputChange}
                    className="w-5 h-5 text-[#0395fc] border-2 border-gray-300 rounded focus:ring-[#0395fc]"
                  />
                  <span className="font-bold text-gray-700">Feature this article on homepage</span>
                </label>
              </div>
            </div>

            {/* Image Upload */}
            <div className="mt-6">
              <label className="block text-sm font-bold text-gray-700 mb-3">Featured Image (Optional)</label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                {selectedImage ? (
                  <div className="space-y-4">
                    <img
                      src={selectedImage.preview}
                      alt="Preview"
                      className="max-w-full max-h-48 mx-auto rounded border-2 border-gray-200"
                    />
                    <div className="flex justify-center gap-4">
                      <button
                        type="button"
                        onClick={() => setSelectedImage(null)}
                        className="bg-red-500 text-white px-4 py-2 rounded border-2 border-black font-bold font-[var(--font-dm-sans)] shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:shadow-[1px_1px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[1px] hover:translate-y-[1px] transition-all duration-200 text-sm"
                      >
                        Remove Image
                      </button>
                    </div>
                    <p className="text-sm text-gray-600 font-[var(--font-dm-sans)]">
                      {selectedImage.file.name} ({(selectedImage.file.size / 1024 / 1024).toFixed(2)} MB)
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <Upload className="w-12 h-12 text-gray-400 mx-auto" />
                    <div>
                      <label htmlFor="image-upload" className="cursor-pointer">
                        <span className="bg-[#0395fc] text-white px-6 py-2 rounded border-2 border-black font-bold font-[var(--font-dm-sans)] shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:shadow-[1px_1px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[1px] hover:translate-y-[1px] transition-all duration-200 inline-block">
                          Choose Image
                        </span>
                        <input
                          id="image-upload"
                          type="file"
                          accept="image/*"
                          onChange={handleImageSelect}
                          className="hidden"
                        />
                      </label>
                    </div>
                    <p className="text-sm text-gray-600 font-[var(--font-dm-sans)]">
                      PNG, JPG, GIF up to 5MB
                    </p>
                  </div>
                )}
              </div>
              {imageError && (
                <p className="text-red-600 text-sm mt-2 font-[var(--font-dm-sans)]">{imageError}</p>
              )}
            </div>

            {/* Submit Button */}
            <div className="flex gap-4 pt-4">
              <button
                type="submit"
                disabled={isLoading}
                className="bg-[#0395fc] text-white px-8 py-3 rounded border-2 border-black font-bold font-[var(--font-dm-sans)] shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Creating...' : 'Create Article'}
              </button>
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="bg-gray-500 text-white px-8 py-3 rounded border-2 border-black font-bold font-[var(--font-dm-sans)] shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] transition-all duration-200"
              >
                Cancel
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Articles List */}
      <div className="bg-white p-6 rounded-lg border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
        <h3 className="text-xl font-black font-[var(--font-anton)] mb-4">Recent Articles</h3>
        
        {isLoadingArticles ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#0395fc] mx-auto mb-4" />
            <p className="text-gray-600 font-[var(--font-dm-sans)]">Loading articles...</p>
          </div>
        ) : articles.length > 0 ? (
          <div className="space-y-4">
            {articles.map((article) => (
              <div key={article.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="font-bold text-lg text-gray-900 mb-1">{article.title}</h4>
                    <p className="text-gray-600 text-sm mb-2">
                      {article.members ? `${article.members.first_name} ${article.members.last_name}` : 'DMLS Team'} • 
                      {new Date(article.created_at).toLocaleDateString()} • 
                      {article.article_type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </p>
                    {article.excerpt && (
                      <p className="text-gray-700 text-sm mb-2 line-clamp-2">{article.excerpt}</p>
                    )}
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      {article.status === 'published' && (
                        <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-medium">
                          Published
                        </span>
                      )}
                      {article.is_featured && (
                        <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs font-medium">
                          Featured
                        </span>
                      )}
                      {article.view_count !== undefined && (
                        <span>{article.view_count} views</span>
                      )}
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex items-center gap-2 ml-4">
                    {article.featured_image_url && (
                      <img
                        src={article.featured_image_url}
                        alt={article.title}
                        className="w-16 h-16 object-cover rounded border flex-shrink-0"
                      />
                    )}
                    <button
                      onClick={() => handleDeleteArticle(article.id, article.title)}
                      disabled={isLoading}
                      className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded border border-red-200 hover:border-red-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Delete article"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
            
            {/* Pagination */}
            {totalArticles > articlesPerPage && (
              <div className="flex items-center justify-between mt-6">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded border-2 border-black font-bold font-[var(--font-dm-sans)] shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:shadow-[1px_1px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[1px] hover:translate-y-[1px] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </button>
                
                <span className="text-sm text-gray-600 font-[var(--font-dm-sans)]">
                  Page {currentPage} of {Math.ceil(totalArticles / articlesPerPage)}
                </span>
                
                <button
                  onClick={() => setCurrentPage(prev => prev + 1)}
                  disabled={currentPage >= Math.ceil(totalArticles / articlesPerPage)}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded border-2 border-black font-bold font-[var(--font-dm-sans)] shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:shadow-[1px_1px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[1px] hover:translate-y-[1px] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h4 className="text-lg font-bold text-gray-600 mb-2">No articles yet</h4>
            <p className="text-gray-500 font-[var(--font-dm-sans)]">
              Create your first article to start sharing knowledge with the community.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// Placeholder components for other tabs
const ROLE_LABELS = { admin: 'Admin', executive: 'Executive', member: 'Member' };
const ROLE_COLORS = {
  admin: 'bg-purple-100 text-purple-800 border-purple-300',
  executive: 'bg-blue-100 text-blue-800 border-blue-300',
  member: 'bg-green-100 text-green-800 border-green-300',
};

const TeamTab = ({ canEdit }) => {
  const { member: currentMember, sendInvite, resignUser, deleteUser } = useAuth();

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');

  // Invite modal state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('executive');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteResult, setInviteResult] = useState(null); // { link, error }
  const [copied, setCopied] = useState(false);

  // Confirm action modal state
  const [confirmAction, setConfirmAction] = useState(null); // { type: 'resign'|'delete', member }
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    setLoading(true);
    setFetchError('');
    const { data, error } = await supabase
      .from('members')
      .select('*, member_roles(*, roles(*))')
      .order('created_at', { ascending: true });

    if (error) {
      setFetchError('Failed to load team members.');
    } else {
      setMembers(data || []);
    }
    setLoading(false);
  };

  const getPrimaryRole = (member) => {
    const active = member.member_roles?.filter(mr => !mr.end_date) || [];
    if (!active.length) return null;
    const primary = active.find(mr => mr.is_primary) || active[0];
    return primary?.roles?.name || null;
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    setInviteLoading(true);
    setInviteResult(null);
    const result = await sendInvite(inviteEmail.trim(), inviteRole);
    if (result.success) {
      setInviteResult({ link: result.inviteLink });
    } else {
      setInviteResult({ error: result.error });
    }
    setInviteLoading(false);
  };

  const handleCopyLink = () => {
    if (!inviteResult?.link) return;
    navigator.clipboard.writeText(inviteResult.link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const closeInviteModal = () => {
    setShowInviteModal(false);
    setInviteEmail('');
    setInviteRole('executive');
    setInviteResult(null);
    setCopied(false);
  };

  const handleConfirmAction = async () => {
    if (!confirmAction) return;
    setActionLoading(true);
    setActionError('');

    const result = confirmAction.type === 'resign'
      ? await resignUser(confirmAction.member.id)
      : await deleteUser(confirmAction.member.id);

    if (result.success) {
      setConfirmAction(null);
      fetchMembers();
    } else {
      setActionError(result.error || 'Action failed. Please try again.');
    }
    setActionLoading(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white p-6 rounded-lg border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-black font-[var(--font-anton)]">Team Management</h2>
          <p className="text-sm text-muted-foreground font-[var(--font-dm-sans)] mt-1">
            Manage council members — invite, resign, or remove accounts.
          </p>
        </div>
        {canEdit('users') && (
          <button
            onClick={() => setShowInviteModal(true)}
            className="flex items-center gap-2 bg-[#0395fc] text-white px-4 py-2 border-2 border-black rounded font-bold font-[var(--font-dm-sans)] shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] hover:shadow-[1px_1px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] transition-all duration-150"
          >
            <Mail className="w-4 h-4" />
            Invite Member
          </button>
        )}
      </div>

      {/* Members list */}
      <div className="bg-white rounded-lg border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-[#0395fc]" />
          </div>
        ) : fetchError ? (
          <div className="p-8 text-center text-red-600 font-[var(--font-dm-sans)]">{fetchError}</div>
        ) : members.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground font-[var(--font-dm-sans)]">No members found.</div>
        ) : (
          <div className="divide-y-2 divide-gray-100">
            {members.map((m) => {
              const role = getPrimaryRole(m);
              const isCurrentUser = m.id === currentMember?.id;
              const isActive = m.membership_status === 'active';

              return (
                <div key={m.id} className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors">
                  {/* Avatar + info */}
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="w-10 h-10 bg-[#0395fc] rounded-full flex items-center justify-center border-2 border-black flex-shrink-0">
                      <span className="text-white font-black text-sm font-[var(--font-anton)]">
                        {(m.first_name?.[0] || '?').toUpperCase()}{(m.last_name?.[0] || '').toUpperCase()}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold font-[var(--font-dm-sans)] text-foreground">
                          {m.first_name} {m.last_name}
                        </span>
                        {isCurrentUser && (
                          <span className="text-xs bg-gray-100 border border-gray-300 px-2 py-0.5 rounded font-[var(--font-dm-sans)]">you</span>
                        )}
                        {!isActive && (
                          <span className="text-xs bg-red-100 border border-red-300 text-red-700 px-2 py-0.5 rounded font-[var(--font-dm-sans)]">resigned</span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground font-[var(--font-dm-sans)] truncate">{m.email}</p>
                    </div>
                  </div>

                  {/* Role badge + actions */}
                  <div className="flex items-center gap-3 flex-shrink-0 ml-4">
                    {role && (
                      <span className={`text-xs font-bold border px-2 py-1 rounded font-[var(--font-dm-sans)] ${ROLE_COLORS[role] || ROLE_COLORS.member}`}>
                        {ROLE_LABELS[role] || role}
                      </span>
                    )}

                    {canEdit('users') && !isCurrentUser && (
                      <div className="flex items-center gap-2">
                        {isActive && (
                          <button
                            onClick={() => { setActionError(''); setConfirmAction({ type: 'resign', member: m }); }}
                            className="flex items-center gap-1 text-sm text-amber-700 bg-amber-50 border border-amber-300 px-3 py-1.5 rounded hover:bg-amber-100 transition-colors font-[var(--font-dm-sans)] font-medium"
                            title="Resign member"
                          >
                            <UserMinus className="w-3.5 h-3.5" />
                            Resign
                          </button>
                        )}
                        <button
                          onClick={() => { setActionError(''); setConfirmAction({ type: 'delete', member: m }); }}
                          className="flex items-center gap-1 text-sm text-red-700 bg-red-50 border border-red-300 px-3 py-1.5 rounded hover:bg-red-100 transition-colors font-[var(--font-dm-sans)] font-medium"
                          title="Delete account"
                        >
                          <UserX className="w-3.5 h-3.5" />
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg border-4 border-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] w-full max-w-md p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-black font-[var(--font-anton)]">Invite Member</h3>
              <button onClick={closeInviteModal} className="text-muted-foreground hover:text-foreground transition-colors">
                <XIcon className="w-5 h-5" />
              </button>
            </div>

            {!inviteResult ? (
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="block text-sm font-bold font-[var(--font-dm-sans)]">Email Address</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={e => setInviteEmail(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleInvite()}
                      className="w-full pl-10 pr-4 py-3 border-2 border-black rounded font-[var(--font-dm-sans)] focus:outline-none focus:border-[#0395fc] transition-colors"
                      placeholder="name@email.com"
                      autoFocus
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-bold font-[var(--font-dm-sans)]">Role</label>
                  <select
                    value={inviteRole}
                    onChange={e => setInviteRole(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-black rounded font-[var(--font-dm-sans)] focus:outline-none focus:border-[#0395fc] transition-colors"
                  >
                    <option value="member">Member</option>
                    <option value="executive">Executive</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>

                <button
                  onClick={handleInvite}
                  disabled={inviteLoading || !inviteEmail.trim()}
                  className="w-full flex items-center justify-center gap-2 bg-[#0395fc] text-white py-3 border-2 border-black rounded font-black font-[var(--font-anton)] shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {inviteLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Send className="w-4 h-4" /> GENERATE INVITE</>}
                </button>
              </div>
            ) : inviteResult.error ? (
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-4 bg-red-50 border-2 border-red-400 rounded">
                  <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
                  <p className="text-sm text-red-700 font-[var(--font-dm-sans)]">{inviteResult.error}</p>
                </div>
                <button
                  onClick={() => setInviteResult(null)}
                  className="w-full py-2 border-2 border-black rounded font-bold font-[var(--font-dm-sans)] hover:bg-gray-50 transition-colors"
                >
                  Try Again
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-4 bg-green-50 border-2 border-green-400 rounded">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <p className="text-sm text-green-700 font-[var(--font-dm-sans)]">
                    Invite created for <strong>{inviteEmail}</strong>. Share the link below — it expires in 7 days.
                  </p>
                </div>

                <div className="space-y-2">
                  <label className="block text-xs font-bold font-[var(--font-dm-sans)] text-muted-foreground uppercase tracking-wide">Invite Link</label>
                  <div className="flex gap-2">
                    <input
                      readOnly
                      value={inviteResult.link}
                      className="flex-1 px-3 py-2 border-2 border-black rounded text-sm font-[var(--font-dm-sans)] bg-gray-50 truncate"
                    />
                    <button
                      onClick={handleCopyLink}
                      className={`flex items-center gap-1.5 px-3 py-2 border-2 border-black rounded font-bold text-sm font-[var(--font-dm-sans)] transition-all ${copied ? 'bg-green-100 text-green-700' : 'bg-white hover:bg-gray-50'}`}
                    >
                      {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      {copied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>

                <button
                  onClick={closeInviteModal}
                  className="w-full py-2 border-2 border-black rounded font-bold font-[var(--font-dm-sans)] hover:bg-gray-50 transition-colors"
                >
                  Done
                </button>
              </div>
            )}
          </motion.div>
        </div>
      )}

      {/* Confirm Action Modal */}
      {confirmAction && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg border-4 border-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] w-full max-w-sm p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${confirmAction.type === 'delete' ? 'bg-red-100' : 'bg-amber-100'}`}>
                {confirmAction.type === 'delete'
                  ? <UserX className="w-5 h-5 text-red-600" />
                  : <UserMinus className="w-5 h-5 text-amber-600" />
                }
              </div>
              <div>
                <h3 className="font-black font-[var(--font-anton)] text-lg">
                  {confirmAction.type === 'delete' ? 'Delete Account' : 'Resign Member'}
                </h3>
                <p className="text-sm text-muted-foreground font-[var(--font-dm-sans)]">
                  {confirmAction.member.first_name} {confirmAction.member.last_name}
                </p>
              </div>
            </div>

            <p className="text-sm font-[var(--font-dm-sans)] text-foreground mb-4">
              {confirmAction.type === 'delete'
                ? 'This will permanently delete this account and all associated data. This cannot be undone.'
                : 'This will deactivate the member and end all their active roles. They will no longer be able to access the dashboard.'}
            </p>

            {actionError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-300 rounded text-sm text-red-700 font-[var(--font-dm-sans)]">
                {actionError}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => { setConfirmAction(null); setActionError(''); }}
                disabled={actionLoading}
                className="flex-1 py-2 border-2 border-black rounded font-bold font-[var(--font-dm-sans)] hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmAction}
                disabled={actionLoading}
                className={`flex-1 flex items-center justify-center gap-2 py-2 border-2 border-black rounded font-bold font-[var(--font-dm-sans)] transition-colors disabled:opacity-50 ${confirmAction.type === 'delete' ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-amber-500 text-white hover:bg-amber-600'}`}
              >
                {actionLoading
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : confirmAction.type === 'delete' ? 'Delete' : 'Resign'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

// ── helpers ──────────────────────────────────────────────────────────────────
const ROLE_TYPE_LABELS = { volunteer: 'Volunteer', committee: 'Committee', executive: 'Executive', project_based: 'Project-Based' };
const LOCATION_TYPE_LABELS = { in_person: 'On-site', remote: 'Remote', hybrid: 'Hybrid' };
const APP_STATUS_COLORS = {
  pending:   'bg-yellow-100 text-yellow-800 border-yellow-300',
  reviewing: 'bg-blue-100   text-blue-800   border-blue-300',
  accepted:  'bg-green-100  text-green-800  border-green-300',
  rejected:  'bg-red-100    text-red-800    border-red-300',
};
const CAREER_STATUS_COLORS = {
  draft:     'bg-gray-100   text-gray-700   border-gray-300',
  published: 'bg-green-100  text-green-800  border-green-300',
  closed:    'bg-red-100    text-red-700    border-red-300',
};

const generateSlug = (title) =>
  title.toLowerCase().replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, '-').slice(0, 80);

const EMPTY_FORM = {
  title: '', department: 'General', role_type: 'volunteer',
  description: '', responsibilities: '', requirements: '',
  time_commitment: '', positions_available: 1,
  location_type: 'hybrid', tags: '',
  application_deadline: '', status: 'draft', is_featured: false,
};

const CareersTab = () => {
  const { member } = useAuth();

  // view: 'jobs' | 'form' | 'applications'
  const [view, setView]                     = useState('jobs');
  const [jobs, setJobs]                     = useState([]);
  const [selectedJob, setSelectedJob]       = useState(null);
  const [applications, setApplications]     = useState([]);
  const [selectedApp, setSelectedApp]       = useState(null);
  const [loading, setLoading]               = useState(true);
  const [saving, setSaving]                 = useState(false);
  const [error, setError]                   = useState('');
  const [form, setForm]                     = useState(EMPTY_FORM);
  const [isEditing, setIsEditing]           = useState(false);
  const [confirmDelete, setConfirmDelete]   = useState(null);
  const [deleteLoading, setDeleteLoading]   = useState(false);

  useEffect(() => { fetchJobs(); }, []);

  // ── data fetching ─────────────────────────────────────────────────────────
  const fetchJobs = async () => {
    setLoading(true);
    setError('');
    const { data, error: err } = await supabase
      .from('careers')
      .select('*, job_applications(id)')
      .order('created_at', { ascending: false });
    if (err) setError('Failed to load careers.');
    else setJobs(data || []);
    setLoading(false);
  };

  const fetchApplications = async (job) => {
    setSelectedJob(job);
    setSelectedApp(null);
    setView('applications');
    const { data } = await supabase
      .from('job_applications')
      .select('*')
      .eq('career_id', job.id)
      .order('created_at', { ascending: false });
    setApplications(data || []);
  };

  // ── job form ──────────────────────────────────────────────────────────────
  const openCreate = () => {
    setForm(EMPTY_FORM);
    setIsEditing(false);
    setView('form');
  };

  const openEdit = (job) => {
    setForm({
      title:                job.title,
      department:           job.department || 'General',
      role_type:            job.role_type || 'volunteer',
      description:          job.description,
      responsibilities:     Array.isArray(job.responsibilities)
                              ? job.responsibilities.join('\n')
                              : (job.responsibilities || ''),
      requirements:         Array.isArray(job.requirements)
                              ? job.requirements.join('\n')
                              : (job.requirements || ''),
      time_commitment:      job.time_commitment || '',
      positions_available:  job.positions_available || 1,
      location_type:        job.location_type || 'hybrid',
      tags:                 (job.tags || []).join(', '),
      application_deadline: job.application_deadline || '',
      status:               job.status,
      is_featured:          job.is_featured || false,
    });
    setSelectedJob(job);
    setIsEditing(true);
    setView('form');
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.description.trim()) {
      setError('Title and description are required.');
      return;
    }
    setSaving(true);
    setError('');

    const payload = {
      title:                form.title.trim(),
      slug:                 isEditing ? selectedJob.slug : generateSlug(form.title.trim()),
      department:           form.department,
      role_type:            form.role_type,
      description:          form.description.trim(),
      responsibilities:     form.responsibilities.split('\n').map(s => s.trim()).filter(Boolean),
      requirements:         form.requirements.split('\n').map(s => s.trim()).filter(Boolean),
      time_commitment:      form.time_commitment.trim() || null,
      positions_available:  parseInt(form.positions_available) || 1,
      location_type:        form.location_type,
      tags:                 form.tags.split(',').map(s => s.trim()).filter(Boolean),
      application_deadline: form.application_deadline || null,
      status:               form.status,
      is_featured:          form.is_featured,
      published_at:         form.status === 'published' ? (selectedJob?.published_at || new Date().toISOString()) : null,
    };

    const { error: err } = isEditing
      ? await supabase.from('careers').update(payload).eq('id', selectedJob.id)
      : await supabase.from('careers').insert([payload]);

    if (err) {
      setError(err.message);
    } else {
      await fetchJobs();
      setView('jobs');
    }
    setSaving(false);
  };

  const handleDelete = async (job) => {
    setDeleteLoading(true);
    await supabase.from('careers').delete().eq('id', job.id);
    setConfirmDelete(null);
    setDeleteLoading(false);
    fetchJobs();
  };

  const handleQuickStatus = async (job, newStatus) => {
    await supabase.from('careers').update({
      status: newStatus,
      published_at: newStatus === 'published' ? (job.published_at || new Date().toISOString()) : job.published_at,
    }).eq('id', job.id);
    fetchJobs();
  };

  // ── application actions ───────────────────────────────────────────────────
  const updateAppStatus = async (appId, status) => {
    await supabase.from('job_applications').update({ status }).eq('id', appId);
    setApplications(prev => prev.map(a => a.id === appId ? { ...a, status } : a));
    if (selectedApp?.id === appId) setSelectedApp(prev => ({ ...prev, status }));
  };

  const downloadResume = async (app) => {
    if (!app.resume_url) return;
    const { data } = await supabase.storage.from('resumes').createSignedUrl(app.resume_url, 3600);
    if (data?.signedUrl) window.open(data.signedUrl, '_blank');
  };

  // ── field helper ──────────────────────────────────────────────────────────
  const field = (name) => ({
    value: form[name],
    onChange: (e) => setForm(prev => ({ ...prev, [name]: e.target.value })),
  });

  const inputCls = 'w-full px-3 py-2.5 border-2 border-black rounded font-[var(--font-dm-sans)] focus:outline-none focus:border-[#0395fc] transition-colors text-sm';
  const labelCls = 'block text-sm font-bold font-[var(--font-dm-sans)] mb-1';

  // ── applications view ─────────────────────────────────────────────────────
  if (view === 'applications') {
    return (
      <div className="space-y-4">
        {/* Header */}
        <div className="bg-white p-6 rounded-lg border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] flex items-center gap-4">
          <button onClick={() => { setView('jobs'); setSelectedApp(null); }}
            className="flex items-center gap-1 text-sm font-bold font-[var(--font-dm-sans)] hover:text-[#0395fc] transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-black font-[var(--font-anton)] truncate">{selectedJob?.title}</h2>
            <p className="text-sm text-muted-foreground font-[var(--font-dm-sans)]">{applications.length} application{applications.length !== 1 ? 's' : ''}</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-5 gap-4">
          {/* Application list */}
          <div className="lg:col-span-2 bg-white rounded-lg border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] overflow-hidden">
            {applications.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground font-[var(--font-dm-sans)]">No applications yet.</div>
            ) : (
              <div className="divide-y-2 divide-gray-100">
                {applications.map(app => (
                  <button key={app.id} onClick={() => setSelectedApp(app)}
                    className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${selectedApp?.id === app.id ? 'bg-blue-50 border-l-4 border-[#0395fc]' : ''}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-bold text-sm font-[var(--font-dm-sans)] truncate">{app.applicant_name}</span>
                      <span className={`text-xs font-bold border px-1.5 py-0.5 rounded flex-shrink-0 ${APP_STATUS_COLORS[app.status]}`}>
                        {app.status}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground font-[var(--font-dm-sans)] truncate">{app.applicant_email}</p>
                    <p className="text-xs text-muted-foreground font-[var(--font-dm-sans)] mt-0.5">{new Date(app.created_at).toLocaleDateString()}</p>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Application detail */}
          <div className="lg:col-span-3 bg-white rounded-lg border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] p-6">
            {!selectedApp ? (
              <div className="flex flex-col items-center justify-center h-full py-12 text-muted-foreground font-[var(--font-dm-sans)]">
                <FileCheck className="w-12 h-12 mb-3 opacity-30" />
                <p>Select an application to review</p>
              </div>
            ) : (
              <div className="space-y-5">
                {/* Applicant header */}
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-black font-[var(--font-anton)]">{selectedApp.applicant_name}</h3>
                    <p className="text-sm text-muted-foreground font-[var(--font-dm-sans)]">{selectedApp.applicant_email}</p>
                    {selectedApp.program && (
                      <p className="text-sm font-[var(--font-dm-sans)]">{selectedApp.program}{selectedApp.year_of_study ? ` · ${selectedApp.year_of_study}` : ''}</p>
                    )}
                    {selectedApp.student_id && (
                      <p className="text-xs text-muted-foreground font-[var(--font-dm-sans)]">ID: {selectedApp.student_id}</p>
                    )}
                  </div>
                  {selectedApp.resume_url && (
                    <button onClick={() => downloadResume(selectedApp)}
                      className="flex items-center gap-1.5 text-sm font-bold font-[var(--font-dm-sans)] bg-[#0395fc] text-white px-3 py-1.5 border-2 border-black rounded shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:shadow-none hover:translate-x-[2px] hover:translate-y-[2px] transition-all flex-shrink-0">
                      <Download className="w-3.5 h-3.5" /> Resume
                    </button>
                  )}
                </div>

                {/* Status selector */}
                <div>
                  <p className={labelCls}>Status</p>
                  <div className="flex flex-wrap gap-2">
                    {['pending', 'reviewing', 'accepted', 'rejected'].map(s => (
                      <button key={s} onClick={() => updateAppStatus(selectedApp.id, s)}
                        className={`px-3 py-1 text-xs font-bold border-2 rounded capitalize transition-all ${selectedApp.status === s ? APP_STATUS_COLORS[s] + ' border-current' : 'border-black bg-white hover:bg-gray-50'}`}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Cover letter */}
                <div>
                  <p className={labelCls}>Cover Letter</p>
                  <p className="text-sm font-[var(--font-dm-sans)] whitespace-pre-wrap bg-gray-50 rounded p-3 border border-gray-200 max-h-48 overflow-y-auto">{selectedApp.cover_letter}</p>
                </div>

                {selectedApp.relevant_experience && (
                  <div>
                    <p className={labelCls}>Relevant Experience</p>
                    <p className="text-sm font-[var(--font-dm-sans)] whitespace-pre-wrap bg-gray-50 rounded p-3 border border-gray-200 max-h-36 overflow-y-auto">{selectedApp.relevant_experience}</p>
                  </div>
                )}

                <p className="text-xs text-muted-foreground font-[var(--font-dm-sans)]">
                  Applied {new Date(selectedApp.created_at).toLocaleString()}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── job form view ─────────────────────────────────────────────────────────
  if (view === 'form') {
    return (
      <div className="space-y-4">
        <div className="bg-white p-6 rounded-lg border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] flex items-center gap-4">
          <button onClick={() => setView('jobs')}
            className="flex items-center gap-1 text-sm font-bold font-[var(--font-dm-sans)] hover:text-[#0395fc] transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <h2 className="text-xl font-black font-[var(--font-anton)]">
            {isEditing ? 'Edit Role' : 'Post New Role'}
          </h2>
        </div>

        <div className="bg-white rounded-lg border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border-2 border-red-400 rounded text-sm text-red-700 font-[var(--font-dm-sans)]">{error}</div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Title */}
            <div className="md:col-span-2">
              <label className={labelCls}>Role Title *</label>
              <input type="text" className={inputCls} placeholder="e.g. Events Committee Lead" {...field('title')} />
            </div>

            {/* Department */}
            <div>
              <label className={labelCls}>Department</label>
              <select className={inputCls} {...field('department')}>
                {['Events','Marketing','Technical','Research','Communications','Executive','General'].map(d => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>

            {/* Role type */}
            <div>
              <label className={labelCls}>Role Type</label>
              <select className={inputCls} {...field('role_type')}>
                <option value="volunteer">Volunteer</option>
                <option value="committee">Committee</option>
                <option value="executive">Executive</option>
                <option value="project_based">Project-Based</option>
              </select>
            </div>

            {/* Location type */}
            <div>
              <label className={labelCls}>Location</label>
              <select className={inputCls} {...field('location_type')}>
                <option value="in_person">On-site (Dal Campus)</option>
                <option value="remote">Remote</option>
                <option value="hybrid">Hybrid</option>
              </select>
            </div>

            {/* Time commitment */}
            <div>
              <label className={labelCls}>Time Commitment</label>
              <input type="text" className={inputCls} placeholder="e.g. 2–3 hours/week" {...field('time_commitment')} />
            </div>

            {/* Positions */}
            <div>
              <label className={labelCls}>Positions Available</label>
              <input type="number" min="1" className={inputCls} {...field('positions_available')} />
            </div>

            {/* Deadline */}
            <div>
              <label className={labelCls}>Application Deadline</label>
              <input type="date" className={inputCls} {...field('application_deadline')} />
            </div>

            {/* Description */}
            <div className="md:col-span-2">
              <label className={labelCls}>Description *</label>
              <textarea rows={4} className={inputCls} placeholder="Describe the role and its impact..." {...field('description')} />
            </div>

            {/* Responsibilities */}
            <div className="md:col-span-2">
              <label className={labelCls}>Responsibilities <span className="font-normal text-muted-foreground">(one per line)</span></label>
              <textarea rows={4} className={inputCls} placeholder={"Organize weekly events\nCoordinate with speakers\nManage event logistics"} {...field('responsibilities')} />
            </div>

            {/* Requirements */}
            <div className="md:col-span-2">
              <label className={labelCls}>Requirements <span className="font-normal text-muted-foreground">(one per line)</span></label>
              <textarea rows={3} className={inputCls} placeholder={"Enrolled Dalhousie student\nPassion for machine learning\nStrong communication skills"} {...field('requirements')} />
            </div>

            {/* Tags */}
            <div className="md:col-span-2">
              <label className={labelCls}>Tags <span className="font-normal text-muted-foreground">(comma-separated)</span></label>
              <input type="text" className={inputCls} placeholder="Python, Events, Leadership" {...field('tags')} />
            </div>

            {/* Status + featured */}
            <div>
              <label className={labelCls}>Status</label>
              <select className={inputCls} {...field('status')}>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="closed">Closed</option>
              </select>
            </div>

            <div className="flex items-center gap-3 pt-6">
              <button type="button"
                onClick={() => setForm(prev => ({ ...prev, is_featured: !prev.is_featured }))}
                className={`flex items-center gap-2 px-4 py-2.5 border-2 border-black rounded font-bold text-sm font-[var(--font-dm-sans)] transition-colors ${form.is_featured ? 'bg-yellow-100 text-yellow-800' : 'bg-white'}`}>
                <Star className={`w-4 h-4 ${form.is_featured ? 'fill-yellow-500 text-yellow-500' : ''}`} />
                {form.is_featured ? 'Featured' : 'Mark as Featured'}
              </button>
            </div>
          </div>

          <div className="flex gap-3 mt-6 pt-6 border-t-2 border-gray-100">
            <button onClick={() => setView('jobs')} disabled={saving}
              className="flex-1 py-3 border-2 border-black rounded font-bold font-[var(--font-dm-sans)] hover:bg-gray-50 transition-colors disabled:opacity-50">
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving}
              className="flex-1 flex items-center justify-center gap-2 bg-[#0395fc] text-white py-3 border-2 border-black rounded font-black font-[var(--font-anton)] shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] transition-all disabled:opacity-60">
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : isEditing ? 'SAVE CHANGES' : 'POST ROLE'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── jobs list view ────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      <div className="bg-white p-6 rounded-lg border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-black font-[var(--font-anton)]">Careers</h2>
          <p className="text-sm text-muted-foreground font-[var(--font-dm-sans)] mt-1">Post volunteer and council roles for DMLS.</p>
        </div>
        <button onClick={openCreate}
          className="flex items-center gap-2 bg-[#0395fc] text-white px-4 py-2 border-2 border-black rounded font-bold font-[var(--font-dm-sans)] shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] hover:shadow-[1px_1px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] transition-all">
          <Plus className="w-4 h-4" /> Post Role
        </button>
      </div>

      {error && <div className="p-4 bg-red-50 border-2 border-red-400 rounded text-sm text-red-700 font-[var(--font-dm-sans)]">{error}</div>}

      <div className="bg-white rounded-lg border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#0395fc]" /></div>
        ) : jobs.length === 0 ? (
          <div className="py-16 text-center">
            <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-muted-foreground font-[var(--font-dm-sans)]">No roles posted yet.</p>
          </div>
        ) : (
          <div className="divide-y-2 divide-gray-100">
            {jobs.map(job => {
              const appCount = job.job_applications?.length || 0;
              return (
                <div key={job.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="font-bold font-[var(--font-dm-sans)]">{job.title}</span>
                        {job.is_featured && <Star className="w-4 h-4 fill-yellow-400 text-yellow-400 flex-shrink-0" />}
                        <span className={`text-xs font-bold border px-2 py-0.5 rounded ${CAREER_STATUS_COLORS[job.status]}`}>{job.status}</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground font-[var(--font-dm-sans)] flex-wrap">
                        <span>{job.department}</span>
                        <span>·</span>
                        <span>{ROLE_TYPE_LABELS[job.role_type]}</span>
                        <span>·</span>
                        <span>{LOCATION_TYPE_LABELS[job.location_type]}</span>
                        {job.application_deadline && <><span>·</span><span>Deadline: {new Date(job.application_deadline).toLocaleDateString()}</span></>}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      {/* Quick publish/unpublish */}
                      {job.status === 'published' ? (
                        <button onClick={() => handleQuickStatus(job, 'closed')} title="Close posting"
                          className="text-xs font-bold font-[var(--font-dm-sans)] text-red-600 border border-red-200 px-2 py-1 rounded hover:bg-red-50 transition-colors">
                          Close
                        </button>
                      ) : job.status === 'draft' ? (
                        <button onClick={() => handleQuickStatus(job, 'published')} title="Publish"
                          className="text-xs font-bold font-[var(--font-dm-sans)] text-green-700 border border-green-200 px-2 py-1 rounded hover:bg-green-50 transition-colors">
                          Publish
                        </button>
                      ) : null}

                      {/* Applications button */}
                      <button onClick={() => fetchApplications(job)}
                        className="flex items-center gap-1 text-xs font-bold font-[var(--font-dm-sans)] bg-gray-100 border border-gray-300 px-3 py-1.5 rounded hover:bg-gray-200 transition-colors">
                        <FileCheck className="w-3.5 h-3.5" />
                        {appCount} app{appCount !== 1 ? 's' : ''}
                      </button>

                      <button onClick={() => openEdit(job)} title="Edit"
                        className="p-1.5 border border-gray-200 rounded hover:bg-gray-100 transition-colors">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button onClick={() => setConfirmDelete(job)} title="Delete"
                        className="p-1.5 border border-red-200 rounded hover:bg-red-50 transition-colors text-red-600">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Delete confirm modal */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg border-4 border-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] w-full max-w-sm p-6">
            <h3 className="font-black font-[var(--font-anton)] text-lg mb-2">Delete Role?</h3>
            <p className="text-sm font-[var(--font-dm-sans)] text-muted-foreground mb-5">
              "<strong>{confirmDelete.title}</strong>" and all its applications will be permanently deleted.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDelete(null)} disabled={deleteLoading}
                className="flex-1 py-2 border-2 border-black rounded font-bold font-[var(--font-dm-sans)] hover:bg-gray-50 transition-colors disabled:opacity-50">
                Cancel
              </button>
              <button onClick={() => handleDelete(confirmDelete)} disabled={deleteLoading}
                className="flex-1 flex items-center justify-center gap-2 bg-red-600 text-white py-2 border-2 border-black rounded font-bold font-[var(--font-dm-sans)] hover:bg-red-700 transition-colors disabled:opacity-50">
                {deleteLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

const SettingsTab = ({ user, member }) => (
  <div className="bg-white p-8 rounded-lg border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
    <h2 className="text-2xl font-black font-[var(--font-anton)] mb-4">Settings</h2>
    <p className="text-muted-foreground font-[var(--font-dm-sans)]">
      Account and system settings will be implemented here.
    </p>
  </div>
);

export default Dashboard;